/**
 * useAuth Hook
 *
 * Provides Cognito authentication functionality:
 * - Sign up (email/password)
 * - Sign in (email/password)
 * - Sign out
 * - Social login (Google, Apple)
 * - Email verification
 * - Password reset
 *
 * After Cognito authentication, syncs user data with the backend.
 */
import { useNavigate } from 'react-router-dom';

import { useMutation } from '@tanstack/react-query';

import {
  CognitoTokens,
  confirmForgotPassword,
  confirmSignUp,
  forgotPassword,
  getTokens,
  isPasskeySupported,
  listPasskeys,
  resendConfirmationCode,
  savePasskeyEmail,
  signIn,
  signInWithApple,
  signInWithGoogle,
  signInWithPasskey,
  signOut,
  signUp,
} from '@splitfiesta/platform-web/auth/cognito';
import type { UserSyncResponseDto } from '@splitfiesta/shared';

import { trackLogin, trackLogout, trackSignUp } from '@/lib/analytics';
import { ErrorFactory } from '@/lib/errors';
import { queryClient } from '@/lib/queryClient';

import { useAuthStore } from '@/stores/authStore';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';
const API_VERSION = '/api/v1';

// ============= API Helpers =============

/**
 * Make an authenticated API call
 */
const apiCall = async (
  endpoint: string,
  options: RequestInit = {},
  token?: string | null
) => {
  const url = `${API_BASE_URL}${API_VERSION}${endpoint}`;
  const headers: Record<string, string> = {
    ...(token && { Authorization: `Bearer ${token}` }),
  };

  // Always set Content-Type for POST/PUT/PATCH/DELETE requests
  // This ensures HTTP API Gateway doesn't add a default Content-Type
  const method = (options.method || 'GET').toUpperCase();
  const needsContentType = ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method);
  if (needsContentType) {
    headers['Content-Type'] = 'application/json';
  }

  // Fastify requires a body when Content-Type is application/json
  // Send empty object if no body provided for methods that need Content-Type
  const body = options.body ?? (needsContentType ? '{}' : undefined);

  const response = await fetch(url, {
    ...options,
    body,
    headers: { ...headers, ...options.headers },
  });

  const data = await response.json();

  if (!response.ok) {
    throw ErrorFactory.createApiError(data, {
      endpoint,
      method: options.method || 'GET',
      body: options.body,
      status: response.status,
    });
  }

  return data;
};

/**
 * Sync user with backend after Cognito authentication
 */
const syncUserWithBackend = async (
  idToken: string
): Promise<UserSyncResponseDto> => {
  const response = await apiCall(
    '/auth/sync',
    {
      method: 'POST',
    },
    idToken
  );

  return response.data as UserSyncResponseDto;
};

// ============= Types =============

export interface SignUpParams {
  email: string;
  password: string;
  name: string;
  locale?: string;
}

export interface SignInParams {
  email: string;
  password: string;
}

export interface ConfirmSignUpParams {
  email: string;
  code: string;
  password?: string; // Optional: if provided, auto-login after verification
}

export interface ForgotPasswordParams {
  email: string;
}

export interface ResetPasswordParams {
  email: string;
  code: string;
  newPassword: string;
}

// ============= Hook =============

export function useAuth() {
  const navigate = useNavigate();
  const { setAuthFromDto, clearAuth } = useAuthStore();

  /**
   * Sign up mutation
   * Creates a new Cognito user. User needs to verify email before signing in.
   */
  const signUpMutation = useMutation({
    mutationFn: async (params: SignUpParams) => {
      await signUp(params);
      // Return email and password for the confirmation step (auto-login after verify)
      return { email: params.email, password: params.password };
    },
    onSuccess: result => {
      // Track analytics
      trackSignUp('email');

      // Navigate to email verification page with password in state (for auto-login)
      navigate(`/verify-email?email=${encodeURIComponent(result.email)}`, {
        state: { password: result.password },
      });
    },
  });

  /**
   * Confirm sign up mutation
   * Verifies the email with the code sent to the user
   * If password is provided, auto-login after verification
   */
  const confirmSignUpMutation = useMutation({
    mutationFn: async (params: ConfirmSignUpParams) => {
      // First, confirm the sign up
      await confirmSignUp({ email: params.email, code: params.code });

      // If password provided, auto-login
      if (params.password) {
        const { session } = await signIn({
          email: params.email,
          password: params.password,
        });

        const tokens: CognitoTokens = {
          idToken: session.getIdToken().getJwtToken(),
          accessToken: session.getAccessToken().getJwtToken(),
          refreshToken: session.getRefreshToken().getToken(),
        };

        const syncResult = await syncUserWithBackend(tokens.idToken);
        return { email: params.email, tokens, syncResult, autoLogin: true };
      }

      return { email: params.email, autoLogin: false };
    },
    onSuccess: result => {
      if (result.autoLogin && result.tokens && result.syncResult) {
        // Clear all React Query cache
        queryClient.clear();

        // Update auth store with user data (include refresh token)
        setAuthFromDto(
          result.syncResult.user,
          result.tokens.idToken,
          result.tokens.refreshToken
        );

        // Navigate to home page
        navigate('/');
      } else {
        // No password available, navigate to login page
        navigate('/login?verified=true');
      }
    },
  });

  /**
   * Resend confirmation code mutation
   */
  const resendCodeMutation = useMutation({
    mutationFn: async (email: string) => {
      await resendConfirmationCode(email);
    },
  });

  /**
   * Sign in mutation
   * Authenticates with Cognito and syncs user with backend
   */
  const signInMutation = useMutation({
    mutationFn: async (params: SignInParams) => {
      // Sign in with Cognito
      const { session } = await signIn(params);

      // Get tokens
      const tokens: CognitoTokens = {
        idToken: session.getIdToken().getJwtToken(),
        accessToken: session.getAccessToken().getJwtToken(),
        refreshToken: session.getRefreshToken().getToken(),
      };

      // Sync with backend
      const syncResult = await syncUserWithBackend(tokens.idToken);

      return { tokens, syncResult };
    },
    onSuccess: async ({ tokens, syncResult }) => {
      // Track analytics
      trackLogin('email');

      // Clear all React Query cache when logging in
      queryClient.clear();

      // Check if user has passkeys registered, if so save email for usernameless sign-in
      if (isPasskeySupported()) {
        try {
          const passkeys = await listPasskeys(tokens.accessToken);
          if (passkeys.length > 0) {
            savePasskeyEmail(syncResult.user.email);
          }
        } catch {
          // Ignore errors checking passkeys
        }
      }

      // Update auth store with user data from backend sync
      setAuthFromDto(syncResult.user, tokens.idToken, tokens.refreshToken);

      // Clear any stored redirect path
      sessionStorage.removeItem('redirectAfterLogin');
      sessionStorage.removeItem('settings_auto_detected');

      // Navigate to home page
      navigate('/');
    },
  });

  /**
   * Sign out mutation
   */
  const signOutMutation = useMutation({
    mutationFn: async () => {
      // Get current token for backend logout
      const tokens = await getTokens();

      // Call backend logout (optional, for audit)
      if (tokens?.idToken) {
        try {
          await apiCall('/auth/logout', { method: 'POST' }, tokens.idToken);
        } catch {
          // Ignore backend logout errors
        }
      }

      // Sign out from Cognito
      await signOut();
    },
    onSuccess: () => {
      // Track analytics
      trackLogout();

      // Clear React Query cache
      queryClient.clear();

      // Clear auth store
      clearAuth();

      // Clear session storage
      sessionStorage.clear();

      // Navigate to login page
      navigate('/login');
    },
  });

  /**
   * Forgot password mutation
   * Sends a reset code to the user's email
   */
  const forgotPasswordMutation = useMutation({
    mutationFn: async (params: ForgotPasswordParams) => {
      await forgotPassword(params);
      return { email: params.email };
    },
    onSuccess: result => {
      // Navigate to reset password page
      navigate(`/reset-password?email=${encodeURIComponent(result.email)}`);
    },
  });

  /**
   * Reset password mutation
   * Confirms the new password with the reset code
   */
  const resetPasswordMutation = useMutation({
    mutationFn: async (params: ResetPasswordParams) => {
      await confirmForgotPassword({
        email: params.email,
        code: params.code,
        newPassword: params.newPassword,
      });
    },
    onSuccess: () => {
      // Navigate to login page
      navigate('/login?reset=true');
    },
  });

  /**
   * Passkey sign in mutation
   * Authenticates with WebAuthn passkey and syncs user with backend
   */
  const passkeySignInMutation = useMutation({
    mutationFn: async (username?: string) => {
      // Sign in with passkey
      const tokens = await signInWithPasskey(username);

      // Sync with backend
      const syncResult = await syncUserWithBackend(tokens.idToken);

      return { tokens, syncResult };
    },
    onSuccess: ({ tokens, syncResult }) => {
      // Track analytics
      trackLogin('passkey');

      // Clear all React Query cache when logging in
      queryClient.clear();

      // Save email for future usernameless passkey sign-in
      savePasskeyEmail(syncResult.user.email);

      // Update auth store with user data from backend sync
      setAuthFromDto(syncResult.user, tokens.idToken, tokens.refreshToken);

      // Clear any stored redirect path
      sessionStorage.removeItem('redirectAfterLogin');
      sessionStorage.removeItem('settings_auto_detected');

      // Navigate to home page
      navigate('/');
    },
  });

  return {
    // Sign up
    signUp: signUpMutation.mutate,
    signUpAsync: signUpMutation.mutateAsync,
    isSigningUp: signUpMutation.isPending,
    signUpError: signUpMutation.error,

    // Confirm sign up
    confirmSignUp: confirmSignUpMutation.mutate,
    confirmSignUpAsync: confirmSignUpMutation.mutateAsync,
    isConfirmingSignUp: confirmSignUpMutation.isPending,
    confirmSignUpError: confirmSignUpMutation.error,

    // Resend code
    resendCode: resendCodeMutation.mutate,
    resendCodeAsync: resendCodeMutation.mutateAsync,
    isResendingCode: resendCodeMutation.isPending,
    resendCodeError: resendCodeMutation.error,

    // Sign in
    signIn: signInMutation.mutate,
    signInAsync: signInMutation.mutateAsync,
    isSigningIn: signInMutation.isPending,
    signInError: signInMutation.error,

    // Sign out
    signOut: signOutMutation.mutate,
    signOutAsync: signOutMutation.mutateAsync,
    isSigningOut: signOutMutation.isPending,
    signOutError: signOutMutation.error,

    // Forgot password
    forgotPassword: forgotPasswordMutation.mutate,
    forgotPasswordAsync: forgotPasswordMutation.mutateAsync,
    isSendingResetCode: forgotPasswordMutation.isPending,
    forgotPasswordError: forgotPasswordMutation.error,

    // Reset password
    resetPassword: resetPasswordMutation.mutate,
    resetPasswordAsync: resetPasswordMutation.mutateAsync,
    isResettingPassword: resetPasswordMutation.isPending,
    resetPasswordError: resetPasswordMutation.error,

    // Social login (these redirect to Cognito hosted UI)
    signInWithGoogle,
    signInWithApple,

    // Passkey sign in
    signInWithPasskey: passkeySignInMutation.mutate,
    signInWithPasskeyAsync: passkeySignInMutation.mutateAsync,
    isSigningInWithPasskey: passkeySignInMutation.isPending,
    passkeySignInError: passkeySignInMutation.error,
    isPasskeySupported: isPasskeySupported(),

    // Legacy aliases for backward compatibility
    login: signInMutation.mutate,
    loginAsync: signInMutation.mutateAsync,
    isLoggingIn: signInMutation.isPending,
    loginError: signInMutation.error,

    register: signUpMutation.mutate,
    registerAsync: signUpMutation.mutateAsync,
    isRegistering: signUpMutation.isPending,
    registerError: signUpMutation.error,

    logout: signOutMutation.mutate,
    logoutAsync: signOutMutation.mutateAsync,
    isLoggingOut: signOutMutation.isPending,
    logoutError: signOutMutation.error,
  };
}

export type UseAuthReturn = ReturnType<typeof useAuth>;
